/**
 * Grace period after the pointer or focus leaves: covers small overshoots past the flyout and
 * focus moving between links of the same item.
 */
const CLOSE_DELAY = 50;

/**
 * The item whose flyout is open or still animating closed. Moving to another item while one is
 * active swaps the panels instantly instead of replaying (and overlapping) the reveal.
 * @type {HeaderNavItem | null}
 */
let activeItem = null;

class HeaderNavItem extends HTMLElement {
  /** @type {HTMLElement | null} */
  #trigger = null;

  /** @type {HTMLElement | null} */
  #flyout = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #closeTimer;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #releaseTimer;

  /** @type {HTMLElement | null} */
  #activeChild = null;

  connectedCallback() {
    this.#trigger = this.querySelector('.nav-item__trigger');
    this.#flyout = this.querySelector('.nav-item__flyout');

    this.addEventListener('pointerenter', this.open);
    this.addEventListener('pointerleave', this.scheduleClose);
    this.addEventListener('focusin', this.open);
    this.addEventListener('focusout', this.scheduleClose);
    this.addEventListener('keydown', this.#onKeydown);

    for (const child of this.querySelectorAll('.nav-item__link-item')) {
      const link = child.querySelector('.nav-item__link');
      const hasGrandchildren = child.classList.contains('nav-item__link-item--has-children');
      const onEnter = hasGrandchildren ? () => this.#activateChild(child) : this.#deactivateChild;
      link?.addEventListener('pointerenter', onEnter);
      link?.addEventListener('focusin', onEnter);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#releaseTimer);
    if (activeItem === this) activeItem = null;
  }

  open = () => {
    clearTimeout(this.#closeTimer);
    if (this.hasAttribute('open')) return;

    const previous = activeItem;
    const switching = previous !== null && previous !== this;
    if (switching) previous.close({ instant: true });

    clearTimeout(this.#releaseTimer);
    this.#setOpen(true, switching);
    activeItem = this;
  };

  scheduleClose = () => {
    clearTimeout(this.#closeTimer);
    if (!this.hasAttribute('open')) return;

    // Let a reveal that has started finish before closing, so it never reverses halfway.
    this.#closeTimer = setTimeout(() => this.close(), Math.max(CLOSE_DELAY, this.#transitionLeft()));
  };

  /** @param {{ instant?: boolean }} [options] */
  close = ({ instant = false } = {}) => {
    clearTimeout(this.#closeTimer);
    clearTimeout(this.#releaseTimer);

    this.#setOpen(false, instant);
    this.#deactivateChild();

    if (activeItem !== this) return;
    if (instant) {
      activeItem = null;
    } else {
      // Stay active while the panel animates closed, so moving to another item swaps instantly.
      this.#releaseTimer = setTimeout(() => {
        if (activeItem === this) activeItem = null;
      }, this.#transitionLeft());
    }
  };

  /**
   * Escape dismisses the flyout (WCAG 1.4.13). Focus goes back to the trigger,
   * otherwise it would sit on a now hidden link.
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.hasAttribute('open')) return;
    this.close();
    if (this.#trigger && this.#trigger !== document.activeElement) {
      this.removeEventListener('focusin', this.open);
      this.#trigger.focus();
      this.addEventListener('focusin', this.open);
    }
  };

  /**
   * @param {boolean} open
   * @param {boolean} instant - Skip the clip-path transition (see [data-instant] in the block CSS).
   */
  #setOpen(open, instant) {
    if (instant) this.setAttribute('data-instant', '');
    this.toggleAttribute('open', open);
    this.#trigger?.setAttribute('aria-expanded', String(open));
    if (!instant) return;

    // Resolve styles while transitions are off, so dropping [data-instant] doesn't start one.
    if (this.#flyout) void getComputedStyle(this.#flyout).clipPath;
    this.removeAttribute('data-instant');
  }

  /** @returns {number} Milliseconds left on the flyout's running clip-path transition, 0 when idle. */
  #transitionLeft() {
    const transition = this.#flyout?.getAnimations()[0];
    if (!transition?.effect || transition.currentTime === null) return 0;
    return Math.max(0, Number(transition.effect.getComputedTiming().endTime) - Number(transition.currentTime));
  }

  /** @param {Element} child */
  #activateChild(child) {
    if (this.#activeChild === child) return;
    this.#activeChild?.removeAttribute('data-active');
    child.setAttribute('data-active', '');
    this.#activeChild = /** @type {HTMLElement} */ (child);
  }

  #deactivateChild = () => {
    this.#activeChild?.removeAttribute('data-active');
    this.#activeChild = null;
  };
}

if (!customElements.get('header-nav-item')) {
  customElements.define('header-nav-item', HeaderNavItem);
}
